/**
 * Created by florinia on 16/06/2017.
 */
import BaseService from '../../base/base_service';
import { GAME_GROUP } from '../../../../common/const/game_groups_consts';
import GamesDiagnosticMessages from './games_diagnostic_messages';
import GamesInfoCache from './games_info_cache';
import IGamesInformationService, {
    ILocalizedGameInfo,
    GAMES_INFO,
    RegulationApprovalValues
} from './games_information_service_interface';

import {Check} from '@sparkware/app-utils';

export interface GameImages {
    icon: string;
    strip_pc: string;
    strip_mobile: string;
    background: string;
    arena_icon: string;
    hybrid_icon: string;
}

export type ExternalGameTypeInfo = [string, string] // [ExternalGameTypeForPC, ExternalGameTypeForMobile]
                                    | string        //same value for both
                                    | null
                                    | undefined

export type ExternalGameSubTypeInfo = [string, string]  // [ExternalGameSubTypeForPC, ExternalGameSubTypeForMobile]
                                      | string // same for both PC and Mobile
                                      | null
                                      | undefined



export interface IGameInfoFromBackend {
    Id: number;
    ExtId: ExternalGameTypeInfo;
    ExtSubType?: ExternalGameSubTypeInfo;
    Title: string;
    HasJackpot: boolean;
    Vendor: string;
    Company: string;
    SubGroup: string;
    Group: string;
    GroupId: number;
    NumberOfLines?: number;
    AllowDemo: boolean;
    HasFreeSpins: boolean;
    PlatformType: string;
    HasSideBets: boolean;
    IsMultiHand: boolean;
    MaxBetPerSpot?: number;
    Decks?: number;
    HasBonusGames: boolean;
    Bets: { [id: string]: number[] };
    Approvals: Record<number, [boolean, boolean]>;
    Thumbnail: string;
    Images: GameImages;
    IsEliteLounge: boolean;
    AllowWithdrawOnlyFromReal: boolean;
    MaxBetPerBrand?: number;
    GameThemes?: number[];
}


/**
 * Provides games information like gameType, gameName
 * reading data from the backend and merging it with games overrides
 * provided by the IntegrationService in the gamesMapper.
 * This service is also making use of the SkinService in order to retrieve
 * the games names
 */
class GamesInformationService extends BaseService implements IGamesInformationService {
    static CLASS = 'GamesInformationService';

    private _getGameTypesInfoUri: string;
    private _getAllGameTypesApiUri: string;
    private _gamesDiagnostics: GamesDiagnosticMessages;
    private _gamesInfoCache: GamesInfoCache;
    private _allowUnfilteredGames: boolean;

    themes: { [key: number]: string; };

    init(params) {
        super.init(params);
        this._getGameTypesInfoUri = params.uri + '/getGameTypesInfo';
        this._getAllGameTypesApiUri = params.uri + '/GetAllGameIDs';
        this._gamesDiagnostics = GamesDiagnosticMessages.create();

        this._gamesInfoCache = this._createGamesInfoCache();

        this._gamesInfoCache.appendBackendGamesInfo(params.gamesInfo);
        this._allowUnfilteredGames = params.allowUnfilteredGames;
        this.themes = params.themes;
    }

    dispose() {
        this._gamesInfoCache.dispose();
    }

    private _createGamesInfoCache() {
        return GamesInfoCache.create({
            getServiceFactory: () => this.serviceFactory,
            gamesDiagnostics: this._gamesDiagnostics,
        });
    }

    /**
     * @returns {SkinService}
     * @private
     */
    private _skinService() {
        return this.serviceFactory.skinService();
    }

    /**
     * @returns {Http2Service}
     * @private
     */
    private _httpService() {
        return this.serviceFactory.http2Service();
    }

    getGamesInfoWithoutAnyFiltering(gameTypes: number[]): Promise<GAMES_INFO> {
        gameTypes = gameTypes || [];
        if (gameTypes.length === 0) {
            return Promise.resolve({});
        }

        return this._gamesInfoCache.getGamesInfo(gameTypes).then(resultFromCache => {
            const finalGamesInfo = resultFromCache.gamesInfo;
            if (resultFromCache.notFoundGames.length > 0) {
                return this._readFromServer(resultFromCache.notFoundGames).then(gamesInfo => {
                    return Object.assign(finalGamesInfo, gamesInfo);
                });
            } else {
                return finalGamesInfo;
            }
        });
    }


    /**
     * Retrieves the games information from the backend and merge it
     * with the information from the IntegrationService.
     * The restricted games are not returned.
     *
     * @param gameTypes - an array of game types to query for
     * @returns {Promise<T>} where T is a map where the key is the game type and the value is an object
     * representing game properties.
     * See documentation page http://confluence/display/CGP/getGamesInfo
     */
    getGamesInfo(gameTypes: number[], overrideUnfilteredGames = false): Promise<GAMES_INFO> {
        return this.getGamesInfoWithoutAnyFiltering(gameTypes).then((gamesInfo) => {
            if(overrideUnfilteredGames) {
                return gamesInfo;
            }
            const result: GAMES_INFO = {};

            const currentRegulationType = this.serviceFactory.regulationService().getRegulationCode();

            Object.keys(gamesInfo).forEach(gameType => {
                const gameInfo = gamesInfo[gameType];

                if (gameInfo) {
                    if (!gameInfo.restriction) {
                        this._applyRestriction(gameInfo);
                    }

                    const isGameApproved = this._allowUnfilteredGames || this._isGameApprovedForRegulation(gameInfo, currentRegulationType);
                    if (!isGameApproved) {
                        this._gamesDiagnostics.logGameNotApproved(gameInfo.gameType, `Game "${gameInfo.gameType} - ${gameInfo.title}" is not approved for regulation ${currentRegulationType} on the current platform!`);
                    } else
                        if (gameInfo.isCurrentPlatformAllowed && gameInfo.restriction.isGameAllowedInTheGrid()) {

                            if (this.isGameAllowedAndExistsInHybrid(gameInfo)) {
                                result[gameType] = gameInfo;
                            }
                        }
                }
            });
            return result;
        });
    }

    private _isGameApprovedForRegulation(gameInfo: ILocalizedGameInfo, currentRegulationType: string): boolean | undefined {
        if(Check.isBoolean(gameInfo.approvals)) {
            return gameInfo.approvals;
        }

        const approvals: RegulationApprovalValues = gameInfo.approvals && gameInfo.approvals[currentRegulationType];

        if(Check.isArray(approvals)) {
            return approvals[this._gamesInfoCache.activePlatformIndex]
        }

        return approvals;

    }


    isGameAllowedAndExistsInHybrid(gameInfo: ILocalizedGameInfo): boolean {
        let isGameAllowed = false;

        if (this.serviceFactory.hybridService().isGameApproved(gameInfo.gameType) || gameInfo.gameGroup === 'Live Casino') {
            isGameAllowed = true;
        }

        return isGameAllowed;
    }

    private _applyRestriction(gameInfo: ILocalizedGameInfo) {
        gameInfo.restriction = this.serviceFactory.restrictionsService().getGameRestriction(gameInfo.gameType, gameInfo.gameGroupId);

        if (!gameInfo.restriction.isGameAllowedInTheGrid()) {
            this._gamesDiagnostics.logGameRestricted(gameInfo.gameType, `Game "${gameInfo.gameType} - ${gameInfo.title}" is not allowed in the grid because of the restrictions`);
        } else if (!gameInfo.restriction.isGameAllowedToPlayReal()) {
            this._gamesDiagnostics.logGameRestricted(gameInfo.gameType, `Game "${gameInfo.gameType} - ${gameInfo.title}" is not allowed to play in real mode because of the restrictions`);
        }
    }

    getAllGameTypes(): Promise<number[]> {
        return this._httpService().get<number[]>(this._getAllGameTypesApiUri);
    }

    getGamesSubTypesMappings(gameSubTypes?: string[]): { [key: string]: number } {
        return this._gamesInfoCache.getGamesSubTypesMappings(gameSubTypes);
    }

    /**
     * Retrieve information for all games available in the site
     * @returns {Object}
     */
    getAllGamesInfo(): Promise<GAMES_INFO> {
        return this.serviceFactory.integrationService().getAllGameTypes()
            .then((gamesType) => {
                return this.getGamesInfo(gamesType);
            });
    }

    /**
     *
     * @param gameTypes
     * @param gamesIntegrationOverrides
     * @returns {Promise.<TResult>|Promise<U>|*}
     * @private
     */
    private _readFromServer(gameTypes: number[]): Promise<GAMES_INFO | void> {
        const brandId = this._skinService().getBrandID();
        const country = this.serviceFactory.countryService().getCountry();
        const regulationID = this.serviceFactory.skinService().getRegulationID();
        const apiUri = this._getGameTypesInfoUri;
        return this._httpService().post<{ Games: IGameInfoFromBackend[] }>(apiUri, { BrandID: brandId.toString(), Country: country, regulationID: regulationID, GameTypes: gameTypes.map(gs => gs.toString()) })
            .then(res => {
                this._gamesInfoCache.appendBackendGamesInfo(res.Games || []);
                return this._gamesInfoCache.getGamesInfo(gameTypes).then(cacheResponse => cacheResponse.gamesInfo);

            })
            .catch((err) => {
                this.reThrowError('262', 'Failed to read games information from the server', err, { gameTypes });
                throw err;
            });
    }


    isVideoSlotOrVideoPokerOrScratchCardsGameGroup(gameGroupId: number): boolean {
        return gameGroupId === GAME_GROUP.VIDEO_POKER ||
            gameGroupId === GAME_GROUP.SCRATCH_CARD ||
            this.isVideoSlotOrSlotsGameGroup(gameGroupId)
    }

    isVideoSlotOrSlotsGameGroup(gameGroupId: number): boolean {
        return gameGroupId === GAME_GROUP.VIDEO_SLOT ||
            gameGroupId === GAME_GROUP.SLOT;
    }

    isRouletteOrBlackjackOrBaccarat(gameGroupId: number): boolean {
        return gameGroupId === GAME_GROUP.ROULETTE ||
            gameGroupId === GAME_GROUP.BLACKJACK ||
            gameGroupId === GAME_GROUP.BACCARAT;
    }

    isLiveGamesGroup(gameGroupId: number): boolean {
        return gameGroupId === GAME_GROUP.LIVE_CASINO;
    }

    getDiagnosticMessages() {
        return this._gamesDiagnostics.getAllDiagnosticMessages().join('\n');
    }
}

export default GamesInformationService;
